/**
 * Sprint 21 B1-B5+B7: Competitive Audit Tool — Public Lead Generation Page
 *
 * Enter competitor URL → AI analyzes → side-by-side comparison → CTA to switch.
 * B5: Share results (copy link, LinkedIn, Twitter, Facebook).
 * B7: Compare up to 3 competitors at once. MFM always first column.
 * PUBLIC page — no auth required. Conversion-optimized design.
 * Real apiFetch. CSS vars only.
 */
import { useState, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import {
  Search, CheckCircle, XCircle, ArrowRight, Share2, Sparkles,
  Shield, Zap, Users, Plus, X, Copy, Loader2,
} from "lucide-react";

type AuditState = "input" | "analyzing" | "results";

interface CompetitorResult {
  name: string;
  url: string;
  overallScore: number;
  reviewScore: number;
  pricing: string;
  features: Record<string, boolean>;
}

interface AuditResult {
  id: string;
  competitors: CompetitorResult[];
  mfm: {
    overallScore: number;
    reviewScore: number;
    pricing: string;
    features: Record<string, boolean>;
  };
  featureNames: Array<{ key: string; label: string; category: string }>;
  savings: string;
  shareUrl: string;
}

const ANALYSIS_STEPS = [
  "Checking features...",
  "Reading reviews...",
  "Comparing pricing...",
  "Analyzing tech stack...",
  "Generating report...",
];

const MAX_COMPETITORS = 3;

export default function AuditPage() {
  const [state, setState] = useState<AuditState>("input");
  const [urls, setUrls] = useState<string[]>([""]);
  const [stepIndex, setStepIndex] = useState(0);
  const [result, setResult] = useState<AuditResult | null>(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const addCompetitor = () => {
    if (urls.length < MAX_COMPETITORS) setUrls([...urls, ""]);
  };

  const removeCompetitor = (index: number) => {
    if (urls.length <= 1) return;
    setUrls(urls.filter((_, i) => i !== index));
  };

  const updateUrl = (index: number, value: string) => {
    setUrls(urls.map((u, i) => (i === index ? value : u)));
  };

  const hasValidUrls = urls.some((u) => u.trim().length > 0);

  const startAudit = useCallback(async () => {
    const validUrls = urls.filter((u) => u.trim());
    if (validUrls.length === 0) return;

    setState("analyzing");
    setStepIndex(0);

    // Progress animation
    let step = 0;
    const interval = setInterval(() => {
      step += 1;
      setStepIndex(step);
      if (step >= ANALYSIS_STEPS.length) clearInterval(interval);
    }, 800);

    try {
      const res = await apiFetch<{ data: AuditResult }>("/audit/analyze", {
        method: "POST",
        body: JSON.stringify({ urls: validUrls }),
      });
      clearInterval(interval);
      setStepIndex(ANALYSIS_STEPS.length);
      setResult(res.data);
      setState("results");
    } catch {
      clearInterval(interval);
      setStepIndex(ANALYSIS_STEPS.length);
      setState("results");
      setResult(null);
    }
  }, [urls]);

  const handleCopyLink = async () => {
    if (!result?.shareUrl) return;
    try {
      await navigator.clipboard.writeText(result.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard failed */ }
  };

  const handleSocialShare = (platform: "linkedin" | "twitter" | "facebook") => {
    if (!result) return;
    const text = `I compared ${result.competitors.map((c) => c.name).join(", ")} vs MFM Effect — here's the result.`;
    const url = result.shareUrl;

    const shareUrls: Record<string, string> = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };

    window.open(shareUrls[platform], "_blank", "noopener,noreferrer,width=600,height=400");
    setShowShareMenu(false);
  };

  const mfmWins = result
    ? result.featureNames.filter((f) => {
        const mfmHas = result.mfm.features[f.key];
        const anyCompHas = result.competitors.some((c) => c.features[f.key]);
        return mfmHas && !anyCompHas;
      }).length
    : 0;

  const colCount = (result?.competitors.length ?? 0) + 2; // feature col + MFM + competitors

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-ai,var(--color-primary))] text-white py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">See How You Compare</h1>
          <p className="text-lg opacity-90 mb-8">
            Enter up to {MAX_COMPETITORS} competitor URLs and get an AI-powered competitive analysis in seconds.
          </p>

          {state === "input" && (
            <div className="max-w-xl mx-auto space-y-3">
              {urls.map((url, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => updateUrl(i, e.target.value)}
                    placeholder={`Competitor ${i + 1} URL (e.g., gohighlevel.com)`}
                    className="flex-1 rounded-xl px-5 py-3 text-[var(--color-foreground)] bg-[var(--color-background)] text-sm"
                    onKeyDown={(e) => e.key === "Enter" && startAudit()}
                  />
                  {urls.length > 1 && (
                    <button type="button" onClick={() => removeCompetitor(i)} className="rounded-xl bg-white/10 px-3 hover:bg-white/20">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}

              <div className="flex gap-3 justify-center pt-2">
                {urls.length < MAX_COMPETITORS && (
                  <button
                    type="button"
                    onClick={addCompetitor}
                    className="inline-flex items-center gap-1 rounded-xl bg-white/10 px-4 py-2.5 text-sm hover:bg-white/20"
                  >
                    <Plus className="h-4 w-4" /> Add Competitor
                  </button>
                )}
                <button
                  type="button"
                  onClick={startAudit}
                  disabled={!hasValidUrls}
                  className="inline-flex items-center gap-2 rounded-xl bg-[var(--color-background)] text-[var(--color-primary)] px-6 py-2.5 font-semibold hover:opacity-90 disabled:opacity-50"
                >
                  <Search className="h-5 w-5" /> Audit {urls.filter((u) => u.trim()).length > 1 ? "All" : "Now"}
                </button>
              </div>
            </div>
          )}

          {state === "analyzing" && (
            <div className="max-w-md mx-auto space-y-3">
              <Loader2 className="h-8 w-8 mx-auto animate-spin" />
              <p className="text-lg font-medium">Analyzing {urls.filter((u) => u.trim()).length} competitor{urls.filter((u) => u.trim()).length !== 1 ? "s" : ""}...</p>
              {ANALYSIS_STEPS.map((step, i) => (
                <div key={i} className={`flex items-center gap-2 text-sm ${i <= stepIndex ? "opacity-100" : "opacity-30"}`}>
                  {i < stepIndex ? <CheckCircle className="h-4 w-4" /> : <div className="h-4 w-4 rounded-full border border-white/50" />}
                  {step}
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 text-sm opacity-70">12,000+ audits completed</div>
        </div>
      </div>

      {/* Results */}
      {state === "results" && result && (
        <div className="max-w-5xl mx-auto px-4 py-12 space-y-8">
          {/* Score comparison — multi-column */}
          <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${result.competitors.length + 1}, 1fr)` }}>
            {/* MFM always first */}
            <div className="rounded-xl border-2 border-[var(--color-primary)] bg-[var(--color-primary)]/5 p-6 text-center">
              <div className="text-sm text-[var(--color-primary)] font-medium mb-2">MFM Effect</div>
              <div className="text-5xl font-bold text-[var(--color-primary)]" style={{ fontVariantNumeric: "tabular-nums" }}>{result.mfm.overallScore}</div>
              <div className="text-xs text-[var(--color-primary)] mt-1">/100</div>
              <div className="mt-2 text-xs text-[var(--color-primary)]">{result.mfm.pricing}</div>
            </div>
            {result.competitors.map((comp) => (
              <div key={comp.url} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-center">
                <div className="text-sm text-[var(--color-muted-foreground)] mb-2">{comp.name}</div>
                <div className="text-5xl font-bold text-[var(--color-muted-foreground)]" style={{ fontVariantNumeric: "tabular-nums" }}>{comp.overallScore}</div>
                <div className="text-xs text-[var(--color-muted-foreground)] mt-1">/100</div>
                <div className="mt-2 text-xs text-[var(--color-muted-foreground)]">{comp.pricing}</div>
              </div>
            ))}
          </div>

          {/* Feature comparison table — dynamic columns */}
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] overflow-hidden">
            <div
              className="grid border-b border-[var(--color-border)] bg-[var(--color-surface)] px-6 py-3 text-sm font-medium text-[var(--color-muted-foreground)]"
              style={{ gridTemplateColumns: `2fr repeat(${colCount - 1}, 1fr)` }}
            >
              <div>Feature</div>
              <div className="text-center text-[var(--color-primary)]">MFM Effect</div>
              {result.competitors.map((c) => <div key={c.url} className="text-center">{c.name}</div>)}
            </div>
            {result.featureNames.map((f) => (
              <div
                key={f.key}
                className="grid border-b border-[var(--color-border)] px-6 py-3 text-sm"
                style={{ gridTemplateColumns: `2fr repeat(${colCount - 1}, 1fr)` }}
              >
                <div className="text-[var(--color-foreground)]">
                  {f.label}
                  <span className="ml-2 text-[10px] text-[var(--color-muted-foreground)]">{f.category}</span>
                </div>
                <div className="text-center">
                  {result.mfm.features[f.key]
                    ? <CheckCircle className="h-4 w-4 mx-auto text-[var(--color-success)]" />
                    : <XCircle className="h-4 w-4 mx-auto text-[var(--color-error)]/40" />}
                </div>
                {result.competitors.map((c) => (
                  <div key={c.url} className="text-center">
                    {c.features[f.key]
                      ? <CheckCircle className="h-4 w-4 mx-auto text-[var(--color-muted-foreground)]" />
                      : <XCircle className="h-4 w-4 mx-auto text-[var(--color-error)]/40" />}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {mfmWins > 0 && (
            <div className="text-center text-sm text-[var(--color-success)] font-medium">
              MFM Effect has {mfmWins} exclusive feature{mfmWins !== 1 ? "s" : ""} that no competitor offers
            </div>
          )}

          {/* CTA */}
          <div className="rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-ai,var(--color-primary))] p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-2">Ready to switch?</h2>
            <p className="opacity-90 mb-6">
              We'll import your data in under 10 minutes. Save {result.savings} on your monthly bill.
            </p>
            <a
              href={`/signup?from=audit&competitor=${encodeURIComponent(result.competitors.map((c) => c.name).join(","))}`}
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--color-background)] text-[var(--color-primary)] px-8 py-3.5 font-semibold hover:opacity-90 transition-colors"
            >
              Start Free Trial <ArrowRight className="h-5 w-5" />
            </a>
          </div>

          {/* B5: Share */}
          <div className="flex items-center justify-center gap-3 relative">
            <button
              type="button"
              onClick={() => setShowShareMenu(!showShareMenu)}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
            >
              <Share2 className="h-4 w-4" /> Share This Audit
            </button>

            {showShareMenu && (
              <div className="absolute bottom-full mb-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-3 shadow-lg space-y-2 min-w-[200px]">
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                >
                  <Copy className="h-4 w-4" /> {copied ? "Copied!" : "Copy Link"}
                </button>
                <button
                  type="button"
                  onClick={() => handleSocialShare("linkedin")}
                  className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                >
                  Share on LinkedIn
                </button>
                <button
                  type="button"
                  onClick={() => handleSocialShare("twitter")}
                  className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                >
                  Share on Twitter
                </button>
                <button
                  type="button"
                  onClick={() => handleSocialShare("facebook")}
                  className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                >
                  Share on Facebook
                </button>
              </div>
            )}
          </div>

          {/* New audit */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => { setState("input"); setResult(null); setUrls([""]); }}
              className="text-sm text-[var(--color-primary)] hover:underline"
            >
              Run another audit
            </button>
          </div>
        </div>
      )}

      {/* No results fallback */}
      {state === "results" && !result && (
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <XCircle className="mx-auto h-12 w-12 text-[var(--color-error)] mb-4" />
          <h2 className="text-xl font-semibold text-[var(--color-foreground)] mb-2">Analysis Failed</h2>
          <p className="text-sm text-[var(--color-muted-foreground)] mb-6">We couldn't analyze the provided URL. Please try again.</p>
          <button
            type="button"
            onClick={() => { setState("input"); setUrls([""]); }}
            className="rounded-lg bg-[var(--color-primary)] px-6 py-2 text-sm text-white hover:opacity-90"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Trust badges */}
      <div className="border-t border-[var(--color-border)] py-8 px-4">
        <div className="max-w-3xl mx-auto flex items-center justify-center gap-8 text-sm text-[var(--color-muted-foreground)]">
          <span className="flex items-center gap-1"><Shield className="h-4 w-4" /> GDPR Compliant</span>
          <span className="flex items-center gap-1"><Sparkles className="h-4 w-4" /> AI-Powered</span>
          <span className="flex items-center gap-1"><Users className="h-4 w-4" /> 12,000+ Audits</span>
        </div>
      </div>
    </div>
  );
}
